import { Award, CheckCircle2, Scale } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/reusable_sections/Header";
export default function AboutCredentials() {
  const {
    lang
  } = useLanguage();
  const content = {
    en: {
      eyebrow: "§ 02 — ABOUT & CREDENTIALS",
      heading: "Multidisciplinary Leadership Across Key Jurisdictions",
      overviewP1: "Stegy Group LLC is a Florida-registered consortium and know-how hub spanning family mediation, tax preparation, real estate services, immigration document preparation, certified translation, and Haitian-law / international consulting for English, French, and Haitian Kreyòl speakers.",
      overviewP2: "Led by Maître Quesner Stephen, our firm brings together rigorous academic credentials and cross-border proficiency to guide individuals, families, and businesses through complex administrative and bilateral matters.",
      credentialsHeading: "Verified Credentials",
      credentials: ["Haitian Attorney affiliated with Port-au-Prince Bar", "Florida Bar Certified Foreign Legal Consultant (Haitian Law)", "Florida Supreme Court Certified Family Mediator", "Master's degree in Criminal Justice and Immigration Studies, University of North Florida (UNF)"],
      noticeTitle: "Notice & Regulatory Disclosure",
      disclaimer: "Stegy Group LLC is not a law firm and its staff are not Florida-licensed attorneys. The firm does not provide legal advice or legal representation regarding Florida law or United States federal law.",
      cta: "Book a Consultation"
    },
    fr: {
      eyebrow: "§ 02 — À PROPOS & ACCRÉDITATIONS",
      heading: "Un leadership multidisciplinaire au service de vos démarches",
      overviewP1: "Stegy Group LLC est un consortium et centre d'expertise enregistré en Floride, intervenant en médiation familiale, fiscalité, services immobiliers, préparation de documents d'immigration, traduction certifiée et conseil en droit haïtien et questions internationales pour les locuteurs anglophones, francophones et créolophones.",
      overviewP2: "Dirigé par Maître Quesner Stephen, notre cabinet conjugue rigueur académique et maîtrise transfrontalière pour assister les particuliers, familles et entreprises dans leurs procédures administratives et bilatérales.",
      credentialsHeading: "Accréditations Vérifiées",
      credentials: ["Avocat haïtien, membre affilié du Barreau de Port-au-Prince", "Consultant Juridique Étranger Certifié du Barreau de Floride (Droit Haïtien)", "Médiateur Familial Certifié par la Cour Suprême de Floride", "Master en Justice Criminelle et Études sur l'Immigration, Université de North Florida (UNF)"],
      noticeTitle: "Avis & Divulgation Réglementaire",
      disclaimer: "Stegy Group LLC n'est pas un cabinet d'avocats et son personnel n'est pas composé d'avocats agréés par le Barreau de Floride. Le cabinet ne fournit aucun avis ni conseil juridique relatif au droit de Floride ou au droit américain.",
      cta: "Réserver une consultation"
    },
    ht: {
      eyebrow: "§ 02 — SOU NOU AK AKREDITASYON",
      heading: "Lidèchip miltidisiplinè pou gide tout pwosesis ou yo",
      overviewP1: "Stegy Group LLC se yon konsòsyòm ak yon sant ekspètiz ki anrejistre nan Florid, ki entèvni nan mediyasyon fanmi, preparasyon taks, sèvis imobilye, preparasyon dokiman imigrasyon, tradiksyon sètifye, ak konsiltasyon sou lwa ayisyen ak zafè entènasyonal pou moun ki pale Anglè, Franse, ak Kreyòl Ayisyen.",
      overviewP2: "Anba direksyon Mèt Quesner Stephen, kabinè a pote ansanm konpetans akademik solid ak ekspètiz transfontyè pou sipòte moun, fanmi, ak antrepriz nan tout demach administratif ak bilateral yo.",
      credentialsHeading: "Akreditasyon Verifye",
      credentials: ["Avoka ayisyen, afilye ak Bawo Pòtoprens", "Konsiltan Legal Etranje Sètifye Bawo Florid (Lwa Ayisyen)", "Medyatè Fanmi Sètifye pa Tribinal Siprèm Florid", "Mèt nan Jistis Kriminèl ak Etid sou Imigrasyon, University of North Florida (UNF)"],
      noticeTitle: "Avi & Enfòmasyon Regilasyon",
      disclaimer: "Stegy Group LLC pa yon kabinè avoka epi anplwaye li yo pa avoka ki gen lisans nan Florid. Kabinè a pa bay konsèy legal ni reprezantasyon legal sou lwa Florid oswa lwa Etazini.",
      cta: "Rezève yon konsiltasyon"
    }
  };
  const t = content[lang as "en" | "fr" | "ht"] || content.en;
  return <section data-section-id="about-credentials" id="about-credentials" className="bg-background text-foreground py-24 md:py-32 border-b border-border">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16">
        {/* Eyebrow */}
        <div className="flex items-center gap-3 mb-6 font-mono text-xs tracking-[0.2em] text-primary">
          <span>{t.eyebrow}</span>
        </div>

        {/* Section Heading */}
        <div className="max-w-3xl mb-12">
          <h2 className="font-serif font-normal text-3xl sm:text-4xl md:text-5xl leading-[1.1] text-foreground mb-6" style={{
          letterSpacing: "-0.02em"
        }}>
            {t.heading}
          </h2>
          <div className="w-16 h-px bg-primary mb-6" />
        </div>

        {/* 2-column layout: Overview on left, Credentials + Notice on right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left Column: Narrative & CTA */}
          <div className="lg:col-span-7 space-y-6">
            <p className="font-sans text-base sm:text-lg leading-relaxed text-foreground/90">
              {t.overviewP1}
            </p>
            <p className="font-sans text-base sm:text-lg leading-relaxed text-foreground/80">
              {t.overviewP2}
            </p>

            <div className="pt-4">
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase rounded-lg px-7 py-3.5 h-auto text-[12px] font-medium tracking-[0.12em] transition-colors duration-200">
                <Link href="https://calendar.app.google/Javq5FfWckJk7vEQ9" newTab>
                  {t.cta}
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Credentials card & Non-advice note */}
          <div className="lg:col-span-5 space-y-6">
            <Card className="rounded-lg border border-border bg-card p-6 sm:p-7 space-y-5">
              <div className="flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-widest">
                <Award className="size-4 shrink-0" strokeWidth={1.5} />
                <span className="font-semibold">{t.credentialsHeading}</span>
              </div>

              <ul className="space-y-4">
                {t.credentials.map((cred, idx) => <li key={idx} className="flex items-start gap-3 text-sm sm:text-base text-card-foreground font-sans leading-snug">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" strokeWidth={1.75} />
                    <span>{cred}</span>
                  </li>)}
              </ul>
            </Card>

            <div className="rounded-lg border border-border/80 bg-muted/40 p-5 space-y-2">
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                <Scale className="size-3.5 shrink-0 text-primary" strokeWidth={1.5} />
                <span>{t.noticeTitle}</span>
              </div>
              <p className="font-sans text-xs sm:text-sm leading-relaxed text-muted-foreground italic">
                {t.disclaimer}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}